<template>
  <MenuDropdown
    :items="dropdownItems"
    :selected-label="selectedLabel"
    bottom
    start
    size="md"
    class="mr-4">
    <template #action="{ triggerIcon, selectedLabel: label }">
      <label
        tabindex="0"
        class="btn gap-1.5 border border-gray-400 bg-white btn-sm"
        data-test="benchmark-filter">
        <component
          v-if="triggerIcon"
          :is="triggerIcon"
          class="h-4 w-4 shrink-0" />
        <span class="font-medium">{{ label }}</span>
        <ChevronDown class="h-4 w-4 shrink-0" />
      </label>
    </template>
  </MenuDropdown>
</template>

<script lang="ts">
export default {
  name: 'BenchmarkSelector',
};
</script>

<script setup lang="ts">
import type { Component } from 'vue';
import { computed } from 'vue';
import MenuDropdown, { type MenuItem } from '@/components/v2/MenuDropdown.vue';
import { BookCheck, ChevronDown, CloudLightning, Network, ShieldAlert, Umbrella, Users } from '@lucide/vue';
import Store from '@/store';
import type { BenchmarkOption } from './Benchmarking';

const benchmarkIcons: Record<string, Component> = {
  'D&O': Network,
  EPL: Users,
  Umbrella,
  Cyber: CloudLightning,
  Fiduciary: BookCheck,
  Crime: ShieldAlert,
};

const props = defineProps<{
  benchmarkOptions: BenchmarkOption[];
  selected: string;
}>();

const emit = defineEmits<{
  change: [value: string];
}>();

function hasPermission(opt: BenchmarkOption): boolean {
  return opt.requirements.every((check) => check(Store.state.user));
}

const selectedLabel = computed(() => {
  const opt = props.benchmarkOptions.find((o) => o.link === props.selected);
  return opt?.name ?? 'Benchmarks';
});

const dropdownItems = computed((): MenuItem[] => {
  return props.benchmarkOptions.filter(hasPermission).map((opt) => ({
    label: opt.name,
    icon: benchmarkIcons[opt.name],
    handler: () => emit('change', opt.link),
    testId: `benchmark-${opt.link}`,
  }));
});
</script>
